import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { orderAPI, productAPI } from '../../../services/api';
import { Order, Product } from '../../../types';

export interface EditItem {
  product_id: number;
  product_name: string;
  quantity: number;
  unit: string;
}

export function useEditOrder() {
  const navigate = useNavigate();
  const { isExpired, user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [fetchData, setFetchData] = useState<{
    order: Order | null;
    products: Product[];
    loading: boolean;
  }>({ order: null, products: [], loading: true });
  const { order, products, loading } = fetchData;
  const [formData, setFormData] = useState<{
    items: EditItem[];
    deliveryDate: string;
    notes: string;
  }>({ items: [], deliveryDate: '', notes: '' });
  const { items, deliveryDate, notes } = formData;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const leadDays = user?.min_delivery_lead_days ?? 0;
  const minDeliveryDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + leadDays);
    return d.toISOString().split('T')[0];
  })();
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<number | ''>('');
  const [addQuantity, setAddQuantity] = useState(1);

  useEffect(() => {
    if (id) {
      Promise.all([
        orderAPI.getOrder(parseInt(id)),
        productAPI.getProducts(),
      ]).then(([orderRes, productsRes]) => {
        const o = orderRes.data;
        const pData = Array.isArray(productsRes.data)
          ? productsRes.data
          : (productsRes.data as any).results || [];
        setFetchData({ order: o, products: pData, loading: false });
        setFormData({
          notes: o.notes || '',
          deliveryDate: o.delivery_date || '',
          items: (o.items || []).map((item: any) => ({
            product_id: item.product.id,
            product_name: item.product.name,
            quantity: item.quantity,
            unit: item.product.unit,
          })),
        });
      }).catch(() => {
        setError('Failed to load order details.');
        setFetchData(prev => ({ ...prev, loading: false }));
      });
    }
  }, [id]);

  const updateItemQuantity = (index: number, qty: number) => {
    if (qty < 1) return;
    setFormData(prev => ({ ...prev, items: prev.items.map((item, i) => i === index ? { ...item, quantity: qty } : item) }));
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) {
      setError('Order must have at least one item.');
      return;
    }
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const handleAddProduct = () => {
    if (!selectedProduct) return;
    const product = products.find(p => p.id === selectedProduct);
    if (!product) return;

    // Check if product already in items
    const existing = items.findIndex(i => i.product_id === product.id);
    if (existing >= 0) {
      updateItemQuantity(existing, items[existing].quantity + addQuantity);
    } else {
      setFormData(prev => ({ ...prev, items: [...prev.items, {
        product_id: product.id,
        product_name: product.name,
        quantity: addQuantity,
        unit: product.unit,
      }] }));
    }
    setShowAddModal(false);
    setSelectedProduct('');
    setAddQuantity(1);
  };

  const handleSave = async () => {
    if (items.length === 0) {
      setError('Order must have at least one item.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await orderAPI.editOrder(parseInt(id!), {
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        delivery_date: deliveryDate || null,
        notes,
      });
      setSuccess('Order updated successfully!');
      setTimeout(() => navigate(`/orders/${id}`), 1200);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update order.');
    } finally {
      setSaving(false);
    }
  };

  // Products not yet in order (for add modal)
  const availableProducts = products.filter(
    p => p.is_available && !items.find(i => i.product_id === p.id)
  );

  return {
    navigate,
    isExpired,
    id,
    order,
    loading,
    items,
    deliveryDate,
    notes,
    saving,
    error,
    setError,
    success,
    showAddModal,
    setShowAddModal,
    selectedProduct,
    setSelectedProduct,
    addQuantity,
    setAddQuantity,
    minDeliveryDate,
    leadDays,
    updateItemQuantity,
    removeItem,
    handleAddProduct,
    handleSave,
    setFormData,
    availableProducts,
  };
}
